import { useState, useCallback } from 'react';
import { useMutation } from 'convex/react';
import { api } from '../../../backend/convex/_generated/api';

interface UseImageUploadReturn {
  isDragging: boolean;
  isUploading: boolean;
  handleDragOver: (e: React.DragEvent) => void;
  handleDragLeave: () => void;
  handleDrop: (e: React.DragEvent, currentTitle: string, onSuccess: (url: string, suggestedTitle?: string) => void) => Promise<void>;
}

export function useImageUpload(): UseImageUploadReturn {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  
  const generateImageUploadUrl = useMutation(api.publisher.generateImageUploadUrl);
  const saveCoverImage = useMutation(api.publisher.saveCoverImage);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (
    e: React.DragEvent, 
    currentTitle: string, 
    onSuccess: (url: string, suggestedTitle?: string) => void
  ) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    const imageFile = Array.from(files).find(f => f.type.startsWith('image/'));
    if (!imageFile) return;
    
    setIsUploading(true);
    try {
      const uploadUrl = await generateImageUploadUrl({});
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: { 'Content-Type': imageFile.type },
        body: imageFile,
      });
      if (!response.ok) throw new Error('Upload failed');
      const { storageId } = await response.json();
      const result = await saveCoverImage({ storageId, bookTitle: currentTitle || 'untitled' });
      
      // Suggest title from filename if no title exists
      let suggestedTitle: string | undefined;
      if (!currentTitle) {
        const name = imageFile.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
        suggestedTitle = name.charAt(0).toUpperCase() + name.slice(1);
      }
      
      onSuccess(result.url, suggestedTitle);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      alert('Upload failed: ' + message);
    } finally {
      setIsUploading(false);
    }
  }, [generateImageUploadUrl, saveCoverImage]);

  return {
    isDragging,
    isUploading,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  };
}
